import React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

/**
 * Reusable name-input dialog used for "Save filter as" and "Rename filter".
 *
 * Props:
 *   open           - boolean
 *   onClose()      - called on cancel / backdrop click
 *   onConfirm(name) - called with the trimmed name string
 *   title          - dialog heading (default: 'Save filter as')
 *   initialName    - pre-filled value when the dialog opens (default: '')
 *   saving         - disables inputs and shows spinner when true
 *   maxLength      - optional character limit for the filter name
 */
const SaveAsDialog = ({
    open,
    onClose,
    onConfirm,
    title = 'Save filter as',
    initialName = '',
    saving = false,
    maxLength,
}) => {
    const [name, setName] = React.useState('');

    React.useEffect(() => {
        if (open) {
            const next = maxLength != null ? initialName.slice(0, maxLength) : initialName;
            setName(next);
        }
    }, [open, initialName, maxLength]);

    const trimmedName = name.trim();
    const isNameTooLong = maxLength != null && trimmedName.length > maxLength;

    const handleConfirm = () => {
        if (!trimmedName || isNameTooLong) return;
        onConfirm(trimmedName);
        setName('');
    };

    const handleClose = () => {
        setName('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <form onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Filter name"
                        value={name}
                        onChange={(e) => {
                            const next = e.target.value;
                            setName(maxLength != null ? next.slice(0, maxLength) : next);
                        }}
                        disabled={saving}
                        error={isNameTooLong}
                        helperText={maxLength != null ? `${trimmedName.length}/${maxLength}` : undefined}
                        inputProps={maxLength != null ? { maxLength } : undefined}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!trimmedName || isNameTooLong || saving}
                        startIcon={saving ? <CircularProgress size={14} /> : undefined}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SaveAsDialog;
